/**
 * Desktop view (`lg` and above) of the check-inventory modal, rendered by
 * `./CheckInventoryModal.tsx`: a `Modal` holding either the batch table (columns from
 * `./CheckInventoryModalTable/CheckInventoryModalTableConfig.tsx`) and a Cancel button, or the
 * "not available locally" message. Closing by the X or Cancel runs `handleOnClose`, which reports
 * back to the caller.
 */
import { css } from "@emotion/react";
import styled from "@emotion/styled";
import { useApplication } from "@ryerson/frontend.application";
import { Flex } from "@ryerson/frontend.layout";
import { Modal } from "@ryerson/frontend.modal";
import { DataTable } from "@ryerson/frontend.table";
import { useTheme } from "@ryerson/frontend.theme";
import React, { useMemo } from "react";
import { getCheckInventoryModalTableConfiguration } from "./CheckInventoryModalTable/CheckInventoryModalTableConfig";
import {
	CheckInventoryModalProps,
	renderItemNotAvailableLocallyMessage,
} from "./CheckInventoryHelper";
import { Button } from "@ryerson/frontend.button";

const DataTableStyler = styled.div`
	${(props: any) => {
		const { borderColor, batches } = props;
		return css`
			border-left: 1px solid ${borderColor};
			border-right: 1px solid ${borderColor};
			border-bottom: ${batches.length ? "none" : `1px solid ${borderColor}`};
		`;
	}};
	& thead > tr > td:first-child {
		.header-content-outer {
			width: 100%;
			justify-content: flex-start;
		}
	}
	& thead > tr > td:last-child {
		.header-content-outer {
			width: 100%;
			justify-content: flex-end;
		}
	}
	& thead > tr > td:not(:first-child):not(:last-child) {
		.header-content-outer {
			width: 100%;
			justify-content: center;
		}
	}
`;

/**
 * Batch-level inventory in a `Modal` with a `DataTable`; Cancel and X both call `handleOnClose`.
 */
const CheckInventoryModalDesktop: React.FC<CheckInventoryModalProps> = ({
	showCheckInventoryModal,
	content,
	batches,
	batchQtyUom,
	batchPriceUom,
	product,
	fetching,
	handleOnClose,
	showItemNotAvailableLocallyMessage,
	isClosable = true,
}) => {
	const {
		localization: { language, country },
	} = useApplication();
	const { theme } = useTheme();

	const { lightGray, darkGray } = theme.colors.primary;

	let batchesDataTableConfig = useMemo(
		() =>
			getCheckInventoryModalTableConfiguration(batchQtyUom, batchPriceUom, product, fetching),
		[batchQtyUom, batchPriceUom, product, fetching]
	);

	return (
		<Modal
			title={content.batchLevelInfo}
			dataTestId="check-inventory-modal"
			type="primary"
			modalStyle="default"
			isOpen={showCheckInventoryModal}
			closable={isClosable}
			onClose={() => handleOnClose()}
			buttonArea={<></>}
		>
			<Flex css={{ width: "540px" }} direction="column">
				{showItemNotAvailableLocallyMessage ? (
					renderItemNotAvailableLocallyMessage(content, theme, country)
				) : (
					<>
						{/* <Flex
							alignItems="center"
							css={{ background: gray, height: "59px", paddingLeft: "20px" }}
						>
							<Typography css={{ paddingRight: "20px" }} color={white}>
								{content.iNeed}
							</Typography>
							<FlexItem css={{ width: "100px", marginRight: "20px" }}>
								<NumberInput
									value={
										Number(batchDisplayQuantity) !== 0
											? batchDisplayQuantity?.toString().replace(/^0+/, "")
											: batchDisplayQuantity?.toString()
									}
									onChange={(event) => batchHandleOnChangeQty(event.target.value)}
									size="sm"
									language={language}
									dataTestId="qty"
								/>
							</FlexItem>
							<FlexItem css={{ width: "80px" }}>
								<Dropdown
									id="uom"
									value={batchQtyUom}
									options={qtyUomDropdown}
									onChange={(value) => batchUpdateQuantityUom(value as UOM)}
									size="sm"
									disabled={qtyUomDropdown?.length === 1}
									language={language}
								/>
							</FlexItem>
						</Flex> */}

						<DataTableStyler {...{ borderColor: lightGray, batches }}>
							<DataTable
								language={language}
								data={batches}
								config={batchesDataTableConfig}
								suppressMobileView={true}
								dataTestId="check-inventory-modal-table"
							/>
						</DataTableStyler>
						<Flex css={{ marginTop: 20, width: "100%" }} justifyContent="center">
							<Button
								label={content.cancel}
								onClick={() => handleOnClose()}
								paddingType="relaxed"
								fullwidth
								foreground="light"
								background={darkGray}
								dataTestId="check-inventory-modal-cancel-button"
							/>
						</Flex>
					</>
				)}
			</Flex>
		</Modal>
	);
};

export default CheckInventoryModalDesktop;
